'use client'

/**
 * AI Chat Panel (Issue #4429, #4443, #4435)
 *
 * Non-modal slide-over panel with refined visual design.
 * Persists across page navigation via root layout provider.
 * Auto-generates conversation titles after first exchange.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { X } from '@/components/icons'
import { usePresentationMode } from '@/contexts/presentation-mode-context'
import { cn } from '@/lib/utils'
import { useAiChat, shouldHideAiChat } from './ai-chat-provider'
import { AiChatMessages } from './ai-chat-messages'
import { AiChatConversationList } from './ai-chat-conversation-list'
import { AiChatInput } from './ai-chat-input'
import {
  useWorkspaceActionCoordinator,
  WorkspaceActionHost,
} from '@/components/workspace-context/workspace-action-host'
import { useVirtualKeyboard } from '@/hooks/use-virtual-keyboard'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import { useSidecarReservation } from '@/components/app-frame'
import { useViewportMinWidth } from '@/hooks/use-viewport-min-width'
import { useRouteErrorActive } from '@/hooks/use-route-error-active'
import {
  AI_CHAT_DOCK_MIN_VIEWPORT_PX,
  AI_CHAT_SIDECAR_WIDTH_PX,
  AI_CHAT_TALL_HEADER_MIN_VIEWPORT_PX,
  AI_CHAT_TRANSITION_MS,
} from './ai-chat-layout'
import { SaiChatDotAnimated } from './sai-chat-dot-animated'
import { useChatAttachments } from './use-chat-attachments'
import { DropOverlay } from '@/components/files/drop-overlay'
import { useDropTargetFileIntake } from '@/hooks/use-page-file-intake'
import { Paperclip } from '@/components/icons'
import { useRegisterWorkspaceSurface } from '@/contexts/workspace-context'
import { buildAiChatWorkspaceSurface } from '@/lib/workspace-context/page-surfaces'
import { readAskInChatDetail } from '@/lib/ai-chat/handoff-attachments'

/** The chat's own registration (#16109): constant, so built once. */
const AI_CHAT_WORKSPACE_SURFACE = buildAiChatWorkspaceSurface()

/** #16102 — why a hand-off's files are on the composer, not in a turn. */
const PANEL_CLOSED_REASON = 'The chat panel was closed.'

export function AiChatPanel() {
  const {
    isOpen,
    closeChat,
    activeMessages,
    isLoading,
    // #13406 — the send-and-stream loop moved to `AiChatProvider`. The panel
    // is now one caller of it among others (#13391's Home column is the next),
    // so everything the loop reads — the conversation id, the message setter,
    // the page context, the workspace snapshot accessor — left with it.
    sendMessage,
    isStreaming,
    registerWorkspaceOutcomeHandler,
    // #13407 — a page is hosting the conversation in itself (Home). The panel
    // renders nothing and, just as importantly, reserves no track: a 440px
    // column of blank beside the page that IS the conversation is the layout
    // this issue exists to remove.
    isInPageChatActive,
    setActiveMessages,
  } = useAiChat()

  const pathname = usePathname()
  // useSearchParams can return null during prerender — the helper handles
  // null safely (#5520, #6460).
  const searchParams = useSearchParams()
  // Issue #7788 — suppress the panel render when presentation mode is
  // active, mirroring the FAB's gate so a still-open panel doesn't overlay
  // a presented slide after the user toggles presentation mode on.
  const { isPresentationMode } = usePresentationMode()
  const { keyboardHeight, isKeyboardOpen } = useVirtualKeyboard(isOpen)

  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const isMobile = useIsMobile()

  // #16101 — the files on the next message: the input's attach button and
  // paste, and the panel as a drop target. Part of AI chat for every org
  // (#16724). The drop is the PANEL's — its handlers stop propagation, so a
  // file dropped on the open panel over `/notes/[id]` goes to chat and never
  // reaches the note page's window-level intake. Above every early return
  // below: hooks are not conditional.
  const files = useChatAttachments({ surface: 'panel' })
  const fileDrop = useDropTargetFileIntake({
    enabled: true,
    onFiles: dropped => files.addFiles(dropped, 'drop'),
  })

  // #12946 — the panel is `fixed`, so it never participates in the frame's
  // grid; instead it RESERVES a track of its own width and the application
  // pane shrinks to `minmax(0, 1fr)` beside it. Hidden routes and presentation
  // mode reserve nothing for the same reason they render nothing.
  //
  // Three conditions gate the reservation, and the WIDTH one is not the mobile
  // breakpoint. `minmax(0, 1fr)` has no floor, so the pane is simply whatever
  // 440px leaves behind — and `!isMobile` only rules out viewports under 640px,
  // which let a 720px window dock and render a full desktop layout into a 280px
  // pane. `AI_CHAT_DOCK_MIN_VIEWPORT_PX` is the real gate; below it the panel
  // reserves nothing and stays the fixed overlay it was before the frame
  // existed. See that constant for why 1080.
  //
  // `!isMobile` is kept alongside it even though 1080 > 640 makes it currently
  // redundant: the mobile branch is a full-screen dialog, and it should win on
  // its own terms rather than by an arithmetic accident between two constants
  // that someone may later retune independently.
  //
  // This must sit ABOVE the `if (!isOpen || …) return null` guard below —
  // hooks cannot be called conditionally, and releasing the track is the
  // whole job on the closed branch.
  const canDockSidecar = useViewportMinWidth(AI_CHAT_DOCK_MIN_VIEWPORT_PX)
  const dockLeavesWideAppPane = useViewportMinWidth(
    AI_CHAT_TALL_HEADER_MIN_VIEWPORT_PX,
  )
  // #13956 — the FAB's other half. A panel left docked over a segment's
  // `error.tsx` is 440px of chat beside a card saying the page did not load,
  // offering to answer questions about the tree that threw. Suppression also
  // releases the sidecar track below, so the error card gets the viewport
  // back rather than a column of blank beside it.
  const isRouteErrorActive = useRouteErrorActive()
  const isSuppressed = shouldHideAiChat(
    pathname,
    searchParams,
    isPresentationMode || isInPageChatActive || isRouteErrorActive,
  )
  const [isPanelRendered, setIsPanelRendered] = useState(isOpen)
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const [hasHydrated, setHasHydrated] = useState(false)

  // Home can register before this streamed sibling hydrates. Match the
  // server's empty closed-panel markup first, then mount its action host.
  useEffect(() => {
    setHasHydrated(true)
  }, [])

  useEffect(() => {
    if (isOpen && !isSuppressed) {
      setIsPanelRendered(true)
      const frame = window.requestAnimationFrame(() => {
        setIsPanelVisible(true)
      })
      return () => window.cancelAnimationFrame(frame)
    }

    setIsPanelVisible(false)
    const timeout = window.setTimeout(() => {
      setIsPanelRendered(false)
    }, AI_CHAT_TRANSITION_MS)
    return () => window.clearTimeout(timeout)
  }, [isOpen, isSuppressed])

  // #16312 — `isDockLayout` drives the root's `data-docked`, which keeps the
  // opaque overlay base off the docked sidecar (see the root className). It
  // deliberately ignores `isOpen`: closing flips `isOpen` at once while the
  // panel is still visible for its slide-out, and a docked panel must not
  // flash to the overlay base on the way out. The track reservation does
  // follow `isOpen`, so the page gets its width back on close.
  const isDockLayout = !isMobile && canDockSidecar && !isSuppressed
  useSidecarReservation(isOpen && isDockLayout ? AI_CHAT_SIDECAR_WIDTH_PX : 0)

  // The panel is `w-full h-screen h-dvh fixed` below the `sm` breakpoint
  // (full-viewport overlay on phones) and `sm:w-[440px]` on desktop
  // (non-occluding side panel). `h-dvh` (100dvh) tracks the dynamic viewport
  // height on mobile browsers with collapsible toolbars; `h-screen` (100vh) is
  // the static-viewport fallback for older browsers. Tailwind v4 emits `h-dvh`
  // after `h-screen` in the generated stylesheet (same-property pair ordering),
  // so `dvh` wins where supported and `vh` applies as the fallback — this order
  // is load-bearing; do not swap them. Lock body scroll only on the mobile
  // branch — same pattern as `CompanyInfoModal` (#6798).
  useBodyScrollLock(isOpen && isMobile)
  // #6841 — trap Tab inside the panel on mobile only. On desktop the panel
  // is a 440px sidebar that coexists with page content, so trapping focus
  // would harm power users; on mobile it covers the full viewport, so the
  // page behind it is non-visible and Tab MUST stay inside.
  useFocusTrap(panelRef, isOpen && isMobile && isPanelRendered)

  // #6841 — focus-restore: capture the previously-focused element BEFORE
  // useFocusTrap moves focus into the panel, then refocus it on close.
  // useLayoutEffect runs synchronously after DOM mutations and BEFORE any
  // useEffect (including useFocusTrap's), guaranteeing the activeElement
  // read on open is the original trigger, not the close button the trap
  // will land on. Pattern mirrors keyboard-shortcuts-drawer.tsx (#6693)
  // exactly — keyed only on `isOpen` so a viewport resize while the panel
  // is open does not yank focus back to the trigger mid-read. The
  // `useFocusTrap(panelRef, isOpen && isMobile)` call above already gates
  // the trap on `isMobile`, which is the right axis for that behavior.
  useLayoutEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current =
        document.activeElement as HTMLElement | null
    } else if (previouslyFocusedRef.current) {
      previouslyFocusedRef.current.focus?.()
      previouslyFocusedRef.current = null
    }
  }, [isOpen])
  // #12949 — the client command coordinator: confirmation prompt, execution
  // of a confirmed capability, reconciliation of the visible application, and
  // the polite announcement.
  //
  // It stays HERE, not in the provider that now owns the stream (#13406),
  // because the confirmation dialog it drives is this component's own UI —
  // `<WorkspaceActionHost coordinator={workspaceActions} />` renders in the
  // tree below, so the pending state and the surface that paints it stay in
  // one place. The panel therefore registers its `handleOutcome` with the loop
  // instead, which is the same late-bound read the old
  // `handleWorkspaceOutcomeRef` mirror performed, expressed across the
  // component boundary the move introduced.
  //
  // What this is NOT justified by: closing the chat does not unmount this
  // component. `AiChatWrapper` mounts it unconditionally, and `isOpen: false`
  // only reaches the `return null` below — which is under this hook call, so
  // `pending` survives a close and is still there on reopen. That behaviour
  // predates #13406 and is not changed here; it is called out so the next
  // reader does not move this hook on the strength of a lifecycle that does
  // not exist.
  //
  // #16109 — the coordinator is told which chat it serves (Home's in-page
  // conversation, or this docked panel) for the note adoption events, and
  // hands back the chat's reply when a confirmed capability made a record:
  // "Created the note … Open the note" lands in the transcript the person is
  // reading, as the server also persists it on the conversation.
  const appendChatReply = useCallback(
    (content: string) => {
      setActiveMessages(previous => [
        ...previous,
        { role: 'assistant', content },
      ])
    },
    [setActiveMessages],
  )
  const workspaceActions = useWorkspaceActionCoordinator({
    surface: isInPageChatActive ? 'home' : 'panel',
    onChatReply: appendChatReply,
  })
  // #16109 — what the chat itself offers, wherever the person is: saving to
  // a new note or adding to one of theirs. Registered here because this
  // component is mounted on every route with chat, Home included (suppressed
  // is not unmounted — see below), and its registration goes when it does.
  useRegisterWorkspaceSurface(AI_CHAT_WORKSPACE_SURFACE)
  const handleWorkspaceOutcome = workspaceActions.handleOutcome
  useEffect(
    () => registerWorkspaceOutcomeHandler(handleWorkspaceOutcome),
    [registerWorkspaceOutcomeHandler, handleWorkspaceOutcome],
  )

  // Escape to close — unless a workspace confirmation is open (#12949).
  //
  // `useModalEscape`'s stack closes the topmost MODAL, but this listener is
  // not in that stack, and it is registered FIRST (the panel mounts before the
  // dialog), so `addEventListener`'s registration order runs it before the
  // stack's handler and before anything calls `preventDefault`. Without the
  // guard, one Escape both dismisses the confirmation and closes the chat,
  // which unmounts the coordinator mid-decision. Checking `defaultPrevented`
  // would not help for exactly the ordering reason above.
  const hasPendingWorkspaceAction = workspaceActions.pending !== null
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !isOpen) return
      if (hasPendingWorkspaceAction) return
      closeChat()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen, closeChat, hasPendingWorkspaceAction])

  // Listen for auto-send events — the ask-bar hand-off (`useAskInChat`).
  // #16102: a hand-off may carry files an ask bar staged; they go into THIS
  // conversation's turn, and a refusal puts them on this composer with the
  // reason (`restore`), since the bar that staged them has already cleared.
  // The detail is read for shape only — see `@/lib/ai-chat/handoff-attachments`.
  const restoreFiles = files.restore
  useEffect(() => {
    const handler = (e: Event) => {
      const raw = (e as CustomEvent).detail
      const detail = readAskInChatDetail(raw)
      if (!detail) return
      const { message, surfaceContext, attachments } = detail
      if (!isOpen) {
        // Closed before the delayed send landed: the question is dropped as
        // it always was, but its files wait on this composer, not nowhere.
        if (attachments) restoreFiles(attachments, PANEL_CLOSED_REASON)
        return
      }
      if (attachments) {
        void sendMessage(message, surfaceContext, {
          attachments,
          source: 'handoff',
          onRejected: reason => restoreFiles(attachments, reason),
        })
      } else if (typeof raw === 'string') {
        void sendMessage(message)
      } else {
        void sendMessage(message, surfaceContext)
      }
    }
    window.addEventListener('ai-chat:send', handler)
    return () => window.removeEventListener('ai-chat:send', handler)
  }, [isOpen, sendMessage, restoreFiles])

  // SUPPRESSED IS NOT UNMOUNTED, and the difference is load-bearing (#13407).
  //
  // `useWorkspaceActionCoordinator` above owns `pending`/`pendingRef`, and the
  // effect beside it registers `handleOutcome` as the send loop's outcome sink
  // whether or not this component paints. So when an in-page surface (Home)
  // suppresses the panel, the sink is still registered — the send loop's
  // "no sink" report never fires — and a `confirmation_required` outcome sets
  // `pending` with no dialog to resolve it and no live region to announce it.
  // Worse, `pendingRef.current` is then non-null forever, and
  // `workspace-action-host.tsx`'s `if (pendingRef.current) return` refuses
  // every later proposal for the rest of the tab.
  //
  // The host renders nothing until a capability needs approving, so mounting
  // it on this path costs a suppressed panel an `sr-only` live region and
  // nothing else.
  // Narrowed to the IN-PAGE case, not every suppressed path. A path-hidden
  // panel (`/pulse/session`, `/meetings`) has nothing driving the send loop,
  // so no outcome can arrive and the host would be an `sr-only` node on a
  // surface that contracts for an empty one —
  // `ai-chat-panel-hidden-paths.test.tsx` asserts exactly that. An in-page
  // surface is the one kind of suppression that keeps sending.
  if (isSuppressed) {
    return isInPageChatActive && hasHydrated ? (
      <WorkspaceActionHost
        coordinator={workspaceActions}
        isTurnStreaming={isStreaming}
      />
    ) : null
  }

  // Unchanged from #12949: chat genuinely closed abandons a pending
  // confirmation rather than leaving one hanging over the application.
  if (!isPanelRendered) {
    return null
  }

  const panelStyle = isKeyboardOpen
    ? { height: `calc(100dvh - ${keyboardHeight}px)` }
    : undefined

  return (
    <div
      ref={panelRef}
      // `sm:w-[440px]` must stay the literal spelling of
      // AI_CHAT_SIDECAR_WIDTH_PX — Tailwind scans source text and cannot read
      // the constant. `ai-chat-panel-sidecar-dock.test.tsx` pins the two
      // together.
      // A plain string, NOT routed through `cn()`, and that is load-bearing.
      // `cn()` is tailwind-merge, which treats the two as the same height
      // utility and keeps only the last — silently deleting the `h-screen`
      // static-viewport fallback that #8021 added on purpose. The pair is not a
      // conflict; it is a progressive-enhancement pair the comment above
      // describes, and `ai-chat-panel-mobile-viewport-height.test.tsx` fails
      // the moment tailwind-merge eats it.
      //
      // `bg-surface-elevated`, NOT `bg-surface-panel` (#16312). In the dark
      // ramp `surface-panel` is a 6% white WASH, which only reads correctly
      // over something opaque. As the < 1080px `fixed` overlay the root sits
      // over live page content, and the page showed through the whole chat,
      // so the overlay root is the opaque base and the wash lives on
      // `ai-chat-panel-surface` below. Docked (`data-docked`), the root stays
      // transparent: the body's own background is what the sidecar has always
      // washed over, and that is darker than `surface-default` in dark mode,
      // so an opaque base there would lighten the docked panel.
      className="fixed right-0 top-0 z-50 flex w-full translate-x-full flex-col border-l border-stroke-light bg-surface-elevated data-[docked=true]:bg-transparent shadow-none transition-transform duration-200 ease-out data-[state=open]:translate-x-0 motion-reduce:transition-none sm:w-[440px] h-screen h-dvh"
      data-state={isPanelVisible ? 'open' : 'closed'}
      data-docked={isDockLayout ? 'true' : undefined}
      style={panelStyle}
      data-testid="ai-chat-panel"
      aria-hidden={isOpen ? undefined : true}
      inert={isOpen ? undefined : true}
      // #6841 — on mobile the panel is a full-viewport overlay with focus trap
      // and Escape-to-close (dialog semantics). On desktop it's a 440px
      // sidebar that coexists with main content (complementary semantics).
      role={isMobile ? 'dialog' : 'complementary'}
      aria-modal={isMobile ? true : undefined}
      aria-label="AI Chat"
      {...fileDrop.handlers}
    >
      {fileDrop.dragging ? (
        <DropOverlay
          placement="panel"
          testId="ai-chat-drop-overlay"
          icon={<Paperclip aria-hidden="true" />}
          title="Drop to attach to your message"
        />
      ) : null}
      {/* #16312 — the panel wash over the root's opaque base. The DropOverlay
          above stays a direct child of the root so it still covers the whole
          panel. The header, conversation list and composer keep their own
          `bg-surface-panel`. Docked, the root is transparent, so this wrapper
          plus their own wash is exactly the stack they painted before over
          the page body: the docked look is unchanged. */}
      <div
        data-testid="ai-chat-panel-surface"
        className="flex min-h-0 flex-1 flex-col bg-surface-panel"
      >
        {/* Header */}
        <div
          data-testid="ai-chat-header"
          className="border-b border-stroke-light bg-surface-panel px-6"
        >
          <div
            data-testid="ai-chat-header-row"
            className={cn(
              'flex items-center justify-between',
              canDockSidecar
                ? dockLeavesWideAppPane
                  ? 'h-16'
                  : 'h-14'
                : 'h-14 md:h-16',
            )}
          >
            <div className="flex items-center gap-3">
              {/* #13332 — `isStreaming` is the reactive mirror of the loop's
                  `isStreamingRef`, set false in `sendMessage`'s `finally`, so
                  every terminal path (success, non-ok response, SSE `error`
                  frame, abort) reaches `thinkingEnd` through one wire. Since
                  #13406 it arrives from context rather than panel state; the
                  wire is otherwise unchanged. */}
              <SaiChatDotAnimated size={24} thinking={isStreaming} />
              <h2 className="text-lg font-semibold text-text-primary">
                Chat with your data
              </h2>
            </div>
            <button
              onClick={closeChat}
              // `hover:bg-action-secondary-hover`, not
              // `hover:bg-surface-panel`: this header IS
              // `bg-surface-panel` (see the wrapper above), so the old
              // hover painted the colour the button already sat on and gave no
              // feedback at all. `--action-secondary-hover` is a
              // translucent neutral wash, so it darkens whatever it sits over.
              //
              // `size-8` is the 32px desktop target; on a touch screen
              // `pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]` lifts
              // it to 44x44. The bracket forms, NOT the equivalent scale-step
              // forms: #6332's contract is pinned by a STATIC scan
              // (`tests/unit/components/close-button-hit-area.test.tsx`) that
              // greps this start tag for those two literals. The scale form
              // renders the identical 44px and reads as an equally good
              // restyle, which is exactly why it is worth a comment — it
              // fails the guard silently as far as the styling is concerned.
              className="inline-flex size-8 pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px] items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-action-secondary-hover hover:text-text-primary"
              aria-label="Close chat"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Conversation List */}
        <AiChatConversationList />

        {/* Messages */}
        <AiChatMessages
          messages={activeMessages}
          isStreaming={isStreaming && isLoading}
        />

        {/* Input — textarea always enabled; only the send action is gated
           while isStreaming is true (spinner replaces send icon). */}
        <AiChatInput
          onSend={(message, extras) => {
            const taken = extras?.attachments ?? []
            if (taken.length === 0) {
              void sendMessage(message)
              return
            }
            void sendMessage(message, undefined, {
              attachments: taken,
              onRejected: reason => files.restore(taken, reason),
            })
          }}
          isStreaming={isStreaming}
          autoFocus
          attachments={files}
        />
      </div>

      {/* #12949 — renders nothing until a capability needs approving, and a
           polite live region the rest of the time. Inside the panel because
           closing the chat should abandon a pending confirmation rather than
           leave one hanging over the application. */}
      <WorkspaceActionHost
        coordinator={workspaceActions}
        isTurnStreaming={isStreaming}
      />
    </div>
  )
}
